import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import { useAuth } from '../../contexts/AuthContext';
import signupImage from '../../assets/images/services-2.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import 'react-phone-input-2/lib/style.css';
import '../common/phone-input-overrides.css';
import './SignupModal.css';

const SignupModal = ({ isOpen, onClose, onSwitchToLogin, onEmailVerification }) => {
  const { signup, signUpWithGoogle } = useAuth();
  const [authType, setAuthType] = useState('email');
  const [phoneAuthMethod, setPhoneAuthMethod] = useState(null);
  const [showPhoneMethodSelection, setShowPhoneMethodSelection] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    termsAgreed: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleAuthInProgress, setGoogleAuthInProgress] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setGoogleAuthInProgress(true);
      setErrors({});
      await signUpWithGoogle();
    } catch (error) {
      setErrors({ general: error.message || 'Google sign-in failed. Please try again.' });
    } finally {
      setIsLoading(false);
      setGoogleAuthInProgress(false);
    }
  };

  const toE164 = (value) => (value ? `+${value}` : '');

  const isValidInternationalPhone = (value) => {
    if (!value) return false;
    const parsed = parsePhoneNumberFromString(toE164(value));
    return !!parsed && parsed.isValid();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (authType === 'email') {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else {
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!isValidInternationalPhone(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }

      const digits = (formData.phone || '').replace(/\D/g, '');
      if (digits.startsWith('91') && !phoneAuthMethod) {
        newErrors.phoneMethod = 'Please select SMS or WhatsApp';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.username && !formData.businessName) {
      newErrors.username = 'Please provide either a username or business name';
      newErrors.businessName = 'Please provide either a username or business name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup({
        [authType]: authType === 'email' ? formData.email : toE164(formData.phone),
        password: formData.password,
        username: formData.username || null,
        businessName: formData.businessName || null,
        authType: authType,
        phoneAuthMethod: authType === 'phone' ? phoneAuthMethod : null
      });

      if (result.success) {
        if (onEmailVerification) {
          onEmailVerification({
            identifier: authType === 'email' ? formData.email : toE164(formData.phone),
            authType,
            method: authType === 'phone' ? phoneAuthMethod : null
          });
        }
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="signup-modal-overlay" onClick={onClose}>
      <div
        className="signup-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${signupImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div className="modal-form-section">
          <div className="form-content" data-lenis-prevent>
            <div className="auth-modal-header text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div className="modal-logo" style={{ marginBottom: '0', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={tecnvironsLogo}
                  alt="Tecnvirons"
                  className="modal-logo-image"
                  style={{
                    width: '80px',
                    maxWidth: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    backgroundColor: 'transparent',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              </div>
              <hr style={{ border: 'none', borderTop: '2px solid #e0e0e0', margin: '15px 0', width: '100%' }} />
              <h2 style={{ marginTop: '0', marginBottom: '15px', fontSize: '22px', fontWeight: '600', whiteSpace: 'normal', display: 'block', width: '100%', textAlign: 'center', wordBreak: 'break-word' }}>ERP + CRM Platform</h2>
            </div>

            <div className="auth-toggle">
              <button className="toggle-btn active">Sign Up</button>
              <button className="toggle-btn" onClick={onSwitchToLogin}>Log In</button>
            </div>

            <div className="form-main">
              <h2 className="form-title text-center">Create Your Account</h2>
              <p className="form-subtitle text-center">Join the Tecnvirons Business Unit platform today</p>

              <div className="social-buttons">
                <button
                  type="button"
                  className="social-btn"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading || googleAuthInProgress}
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" width="18" height="18" />
                  <span style={{ marginLeft: '10px', fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>Sign up with Google</span>
                </button>
              </div>

              <div className="divider">
                <span>or continue with email</span>
              </div>

              {errors.general && (
                <div className="error-message">
                  {errors.general}
                </div>
              )}

              <form onSubmit={handleSubmit} className="signup-form">
                <div className="auth-type-toggle" style={{
                  display: 'flex',
                  gap: '10px',
                  marginBottom: '20px',
                  borderBottom: '2px solid #e0e0e0',
                  paddingBottom: '10px'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthType('email');
                      setFormData(prev => ({ ...prev, phone: '' }));
                      setPhoneAuthMethod(null);
                      setShowPhoneMethodSelection(false);
                      setErrors(prev => ({ ...prev, phone: '' }));
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      background: authType === 'email' ? '#7c3aed' : '#f3f4f6',
                      color: authType === 'email' ? 'white' : '#6b7280',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthType('phone');
                      setFormData(prev => ({ ...prev, email: '' }));
                      setPhoneAuthMethod(null);
                      setShowPhoneMethodSelection(false);
                      setErrors(prev => ({ ...prev, email: '' }));
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      background: authType === 'phone' ? '#7c3aed' : '#f3f4f6',
                      color: authType === 'phone' ? 'white' : '#6b7280',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Phone width={16} height={16} style={{ display: 'inline', marginRight: '5px' }} />
                    Phone
                  </button>
                </div>

                <div className="modal-form-group-full-width">
                  <input
                    type="text"
                    id="username"
                    name="username"
                    placeholder=" "
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <label htmlFor="username">Username</label>
                  {errors.username && <span className="field-error">{errors.username}</span>}
                </div>

                <div className="modal-form-group-full-width">
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    placeholder=" "
                    value={formData.businessName}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <label htmlFor="businessName">Business Name</label>
                  {errors.businessName && <span className="field-error">{errors.businessName}</span>}
                </div>

                {authType === 'email' ? (
                  <div className="modal-form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder=" "
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="email">Email</label>
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                ) : (
                  <>
                    <div className="modal-form-group modal-form-group-phone">
                      <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', color: '#4b5563', fontSize: '14px' }}>Phone Number</label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phone: value }));
                          if (errors.phone) {
                            setErrors(prev => ({ ...prev, phone: '' }));
                          }

                          const digits = (value || '').replace(/\D/g, '');
                          const hasEnoughDigits = digits.length >= 7;
                          const currentIsIndian = digits.startsWith('91');

                          if (!hasEnoughDigits) {
                            setShowPhoneMethodSelection(false);
                            setPhoneAuthMethod(null);
                          } else if (currentIsIndian) {
                            setShowPhoneMethodSelection(true);
                            setPhoneAuthMethod(null);
                          } else {
                            setShowPhoneMethodSelection(false);
                            setPhoneAuthMethod('whatsapp');
                          }
                        }}
                        country="in"
                        countryCodeEditable={true}
                        disableDropdown={false}
                        enableSearch={true}
                        preferredCountries={['in', 'us', 'gb', 'ae', 'sa', 'pk', 'bd']}
                        searchPlaceholder="Search country..."
                        searchNotFound="No country found"
                        disabled={isLoading}
                      />
                      {errors.phone && <span className="field-error">{errors.phone}</span>}
                    </div>

                    {showPhoneMethodSelection && (
                      <div style={{
                        padding: '18px 16px',
                        background: 'linear-gradient(135deg, #f0f4ff 0%, #f8ebff 100%)',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        border: '2px solid #e0e7ff',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)'
                      }}>
                        <p style={{ marginTop: 0, marginBottom: '14px', fontWeight: '700', color: '#4338ca', fontSize: '14px', textAlign: 'center' }}>Select Your OTP Method</p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setPhoneAuthMethod('sms');
                              setShowPhoneMethodSelection(false);
                              setErrors(prev => ({ ...prev, phoneMethod: '' }));
                            }}
                            style={{
                              flex: 1,
                              padding: '13px 16px',
                              border: phoneAuthMethod === 'sms' ? '2px solid #0084ff' : '2px solid #ddd',
                              background: phoneAuthMethod === 'sms' ? 'linear-gradient(135deg, #0084ff 0%, #0066cc 100%)' : '#ffffff',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              color: phoneAuthMethod === 'sms' ? '#ffffff' : '#4b5563',
                              transition: 'all 0.3s ease',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              boxShadow: phoneAuthMethod === 'sms' ? '0 4px 12px rgba(0, 132, 255, 0.35)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                            SMS
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPhoneAuthMethod('whatsapp');
                              setShowPhoneMethodSelection(false);
                              setErrors(prev => ({ ...prev, phoneMethod: '' }));
                            }}
                            style={{
                              flex: 1,
                              padding: '13px 16px',
                              border: phoneAuthMethod === 'whatsapp' ? '2px solid #25d366' : '2px solid #ddd',
                              background: phoneAuthMethod === 'whatsapp' ? 'linear-gradient(135deg, #25d366 0%, #20ba5f 100%)' : '#ffffff',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              color: phoneAuthMethod === 'whatsapp' ? '#ffffff' : '#4b5563',
                              transition: 'all 0.3s ease',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              boxShadow: phoneAuthMethod === 'whatsapp' ? '0 4px 12px rgba(37, 211, 102, 0.35)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                            WhatsApp
                          </button>
                        </div>
                        {errors.phoneMethod && <span className="field-error" style={{ marginTop: '8px', display: 'block', fontSize: '12px' }}>{errors.phoneMethod}</span>}
                      </div>
                    )}
                  </>
                )}

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder=" "
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="password">Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder=" "
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
                </div>

                <div className="form-options">
                  <div className="user-signup-terms-container">
                    <input
                      type="checkbox"
                      id="termsAgreementUser"
                      name="termsAgreed"
                      className="auth-custom-checkbox"
                      checked={formData.termsAgreed}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <label htmlFor="termsAgreementUser" className="user-signup-terms-label">I agree to the Terms of Service and Privacy Policy</label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>

              <div className="form-footer">
                <p>Already have an account? <button type="button" onClick={onSwitchToLogin} className="link-btn">Sign In</button></p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={signupImage} alt="Join Tecnvirons" className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>

            <div className="overlay-content">
              <div className="overlay-card">
                <h3>Join the Platform!</h3>
                <p>Create your account and bring ERP and CRM together under one roof.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Transform Your Business,<br />One Workflow at a Time!</h3>
                <p>Get started with Tecnvirons today!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
